/**
 * NEXUMA — Roles Configuration
 *
 * Defines all standard roles with:
 * - role key (used for matching)
 * - human label
 * - priority (higher = more powerful, used for inheritance checks)
 * - color / badge for UI
 * - exhaustive permission set
 *
 * Role matching is done case-insensitively against the `aliases` array,
 * so existing data like 'admin', 'menaxher', 'manager' continues to work.
 */

import { PERMISSIONS as P } from './permissionsConfig';

// ── Role keys ─────────────────────────────────────────────────────────────────
export const ROLES = {
  SUPER_ADMIN:      'super_admin',
  COMPANY_ADMIN:    'company_admin',
  HR_MANAGER:       'hr_manager',
  PAYROLL_MANAGER:  'payroll_manager',
  RECRUITER:        'recruiter',
  TEAM_LEAD:        'team_lead',
  EMPLOYEE:         'employee',
  AUDITOR:          'auditor',
};

// ── Permission sets per role ──────────────────────────────────────────────────
const SUPER_ADMIN_PERMISSIONS = Object.values(P); // all permissions

const COMPANY_ADMIN_PERMISSIONS = [
  // Onboarding — full
  P.ONBOARDING_VIEW, P.ONBOARDING_CREATE, P.ONBOARDING_EDIT,
  P.ONBOARDING_COMPLETE, P.ONBOARDING_MANAGE,
  P.COMMUNICATION_VIEW_COMPANY, P.COMMUNICATION_CREATE_COMPANY, P.COMMUNICATION_MANAGE,
  // Dashboard — full company
  P.DASHBOARD_VIEW_COMPANY,
  P.DASHBOARD_VIEW_ALL,
  // Employees — full
  P.EMPLOYEES_VIEW_ALL, P.EMPLOYEES_CREATE, P.EMPLOYEES_EDIT_COMPANY,
  P.EMPLOYEES_DELETE, P.EMPLOYEES_EXPORT, P.EMPLOYEES_MANAGE,
  // Contracts — full
  P.CONTRACTS_VIEW_COMPANY, P.CONTRACTS_CREATE, P.CONTRACTS_EDIT,
  P.CONTRACTS_DELETE, P.CONTRACTS_APPROVE, P.CONTRACTS_EXPORT,
  P.CONTRACTS_LOCK, P.CONTRACTS_UNLOCK,
  // Attendance — full
  P.ATTENDANCE_VIEW_COMPANY, P.ATTENDANCE_EDIT_DEPARTMENT,
  P.ATTENDANCE_APPROVE, P.ATTENDANCE_EXPORT, P.ATTENDANCE_MANAGE,
  // Leave — full
  P.LEAVE_VIEW_COMPANY, P.LEAVE_CREATE_COMPANY, P.LEAVE_EDIT_COMPANY,
  P.LEAVE_APPROVE, P.LEAVE_REJECT, P.LEAVE_EXPORT, P.LEAVE_MANAGE,
  P.LEAVE_ADJUSTMENTS_VIEW, P.LEAVE_ADJUSTMENTS_MANAGE,
  // Payroll — full
  P.PAYROLL_VIEW_COMPANY, P.PAYROLL_CREATE, P.PAYROLL_EDIT_COMPANY,
  P.PAYROLL_APPROVE_COMPANY, P.PAYROLL_EXPORT, P.PAYROLL_MANAGE, P.PAYROLL_LOCK,
  // Recruitment — full
  P.RECRUITMENT_VIEW, P.RECRUITMENT_CREATE, P.RECRUITMENT_EDIT,
  P.RECRUITMENT_DELETE, P.RECRUITMENT_APPROVE, P.RECRUITMENT_MANAGE,
  // Performance — full
  P.PERFORMANCE_VIEW_COMPANY, P.PERFORMANCE_CREATE,
  P.PERFORMANCE_EDIT, P.PERFORMANCE_APPROVE, P.PERFORMANCE_MANAGE,
  // Documents — full
  P.DOCUMENTS_VIEW_COMPANY, P.DOCUMENTS_CREATE,
  P.DOCUMENTS_EDIT, P.DOCUMENTS_DELETE, P.DOCUMENTS_EXPORT,
  // Reports — full
  P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL, P.REPORTS_EXPORT,
  // Settings — full
  P.SETTINGS_VIEW, P.SETTINGS_EDIT, P.SETTINGS_MANAGE,
  // Audit — full
  P.AUDIT_LOGS_VIEW_COMPANY, P.AUDIT_LOGS_VIEW_ALL, P.AUDIT_LOGS_EXPORT,
  // Roles — full
  P.ROLES_VIEW, P.ROLES_CREATE, P.ROLES_EDIT, P.ROLES_DELETE,
  P.ROLES_MANAGE, P.ROLES_ASSIGN,
  // Projects — full
  P.PROJECTS_VIEW_COMPANY, P.PROJECTS_CREATE,
  P.PROJECTS_EDIT, P.PROJECTS_MANAGE,
  // Warnings — full
  P.WARNINGS_VIEW_COMPANY, P.WARNINGS_CREATE,
  P.WARNINGS_EDIT, P.WARNINGS_DELETE,
  // Assets — full
  P.ASSETS_VIEW_COMPANY, P.ASSETS_CREATE, P.ASSETS_EDIT,
  P.ASSETS_DELETE, P.ASSETS_EXPORT, P.ASSETS_MANAGE,
  // Compliance
  P.COMPLIANCE_VIEW, P.COMPLIANCE_MANAGE,
  P.TRAININGS_VIEW, P.TRAININGS_CREATE, P.TRAININGS_EDIT, P.TRAININGS_DELETE, P.TRAININGS_EXPORT,
];

const HR_MANAGER_PERMISSIONS = [
  // Onboarding — full
  P.ONBOARDING_VIEW, P.ONBOARDING_CREATE, P.ONBOARDING_EDIT,
  P.ONBOARDING_COMPLETE, P.ONBOARDING_MANAGE,
  P.COMMUNICATION_VIEW_COMPANY, P.COMMUNICATION_CREATE_COMPANY, P.COMMUNICATION_MANAGE,
  P.DASHBOARD_VIEW_COMPANY,
  // Employees — company-wide
  P.EMPLOYEES_VIEW_COMPANY, P.EMPLOYEES_CREATE, P.EMPLOYEES_EDIT_COMPANY,
  P.EMPLOYEES_DELETE, P.EMPLOYEES_EXPORT,
  // Contracts — company
  P.CONTRACTS_VIEW_COMPANY, P.CONTRACTS_CREATE, P.CONTRACTS_EDIT,
  P.CONTRACTS_APPROVE, P.CONTRACTS_EXPORT, P.CONTRACTS_LOCK,
  // Attendance — company management
  P.ATTENDANCE_VIEW_COMPANY, P.ATTENDANCE_EDIT_DEPARTMENT,
  P.ATTENDANCE_APPROVE, P.ATTENDANCE_EXPORT, P.ATTENDANCE_MANAGE,
  // Leave — full management
  P.LEAVE_VIEW_COMPANY, P.LEAVE_CREATE_COMPANY, P.LEAVE_EDIT_COMPANY,
  P.LEAVE_APPROVE, P.LEAVE_REJECT, P.LEAVE_EXPORT, P.LEAVE_MANAGE,
  P.LEAVE_ADJUSTMENTS_VIEW, P.LEAVE_ADJUSTMENTS_MANAGE,
  // Payroll — view only
  P.PAYROLL_VIEW_COMPANY, P.PAYROLL_EXPORT,
  // Recruitment — full
  P.RECRUITMENT_VIEW, P.RECRUITMENT_CREATE, P.RECRUITMENT_EDIT,
  P.RECRUITMENT_DELETE, P.RECRUITMENT_APPROVE, P.RECRUITMENT_MANAGE,
  // Performance — company
  P.PERFORMANCE_VIEW_COMPANY, P.PERFORMANCE_CREATE,
  P.PERFORMANCE_EDIT, P.PERFORMANCE_APPROVE, P.PERFORMANCE_MANAGE,
  // Documents — company
  P.DOCUMENTS_VIEW_COMPANY, P.DOCUMENTS_CREATE,
  P.DOCUMENTS_EDIT, P.DOCUMENTS_DELETE, P.DOCUMENTS_EXPORT,
  // Reports — company
  P.REPORTS_VIEW_COMPANY, P.REPORTS_EXPORT,
  // Settings — view only
  P.SETTINGS_VIEW,
  // Audit — view
  P.AUDIT_LOGS_VIEW_COMPANY,
  // Projects — company
  P.PROJECTS_VIEW_COMPANY, P.PROJECTS_CREATE, P.PROJECTS_EDIT,
  // Warnings — company
  P.WARNINGS_VIEW_COMPANY, P.WARNINGS_CREATE, P.WARNINGS_EDIT,
  // Assets — HR-ja dorëzon dhe pranon; fshirja mbetet e adminit, sepse një aset
  // i fshirë e merr me vete provën se ekzistoi dhe se iu dorëzua dikujt.
  P.ASSETS_VIEW_COMPANY, P.ASSETS_CREATE, P.ASSETS_EDIT,
  P.ASSETS_EXPORT, P.ASSETS_MANAGE,
  // Compliance
  P.COMPLIANCE_VIEW,
  P.TRAININGS_VIEW, P.TRAININGS_CREATE, P.TRAININGS_EDIT, P.TRAININGS_DELETE, P.TRAININGS_EXPORT,
];

const PAYROLL_MANAGER_PERMISSIONS = [
  P.DASHBOARD_VIEW_DEPARTMENT,
  // Employees — view only
  P.EMPLOYEES_VIEW_COMPANY, P.EMPLOYEES_EXPORT,
  // Contracts — view
  P.CONTRACTS_VIEW_COMPANY, P.CONTRACTS_EXPORT,
  // Attendance — view
  P.ATTENDANCE_VIEW_COMPANY, P.ATTENDANCE_EXPORT,
  // Leave — view
  P.LEAVE_VIEW_COMPANY, P.LEAVE_EXPORT,
  P.LEAVE_ADJUSTMENTS_VIEW,
  // Payroll — full
  P.PAYROLL_VIEW_COMPANY, P.PAYROLL_CREATE, P.PAYROLL_EDIT_COMPANY,
  P.PAYROLL_APPROVE_COMPANY, P.PAYROLL_EXPORT, P.PAYROLL_MANAGE, P.PAYROLL_LOCK,
  // Documents — manage payroll docs
  P.DOCUMENTS_VIEW_COMPANY, P.DOCUMENTS_CREATE, P.DOCUMENTS_EXPORT,
  // Reports — company
  P.REPORTS_VIEW_COMPANY, P.REPORTS_EXPORT,
  P.TRAININGS_VIEW, P.TRAININGS_EXPORT,
];

const RECRUITER_PERMISSIONS = [
  // Onboarding — e nis dorëzimin, nuk e mbyll procesin
  P.ONBOARDING_VIEW, P.ONBOARDING_CREATE,
  P.DASHBOARD_VIEW_DEPARTMENT,
  // Employees — view only
  P.EMPLOYEES_VIEW_COMPANY,
  // Recruitment — full
  P.RECRUITMENT_VIEW, P.RECRUITMENT_CREATE, P.RECRUITMENT_EDIT,
  P.RECRUITMENT_DELETE, P.RECRUITMENT_MANAGE,
  // Documents — own
  P.DOCUMENTS_VIEW_OWN, P.DOCUMENTS_CREATE,
  // Reports — own
  P.REPORTS_VIEW_OWN,
  P.TRAININGS_VIEW, P.TRAININGS_CREATE, P.TRAININGS_EDIT,
];

const TEAM_LEAD_PERMISSIONS = [
  P.ONBOARDING_VIEW,
  P.DASHBOARD_VIEW_TEAM,
  // Employees — team
  P.EMPLOYEES_VIEW_TEAM,
  // Attendance — team
  P.ATTENDANCE_VIEW_TEAM, P.ATTENDANCE_APPROVE,
  // Leave — team
  P.LEAVE_VIEW_TEAM, P.LEAVE_APPROVE, P.LEAVE_REJECT,
  // Performance — team
  P.PERFORMANCE_VIEW_TEAM, P.PERFORMANCE_CREATE, P.PERFORMANCE_EDIT,
  // Reports — team
  P.REPORTS_VIEW_DEPARTMENT,
  // Projects — team
  P.PROJECTS_VIEW_TEAM, P.PROJECTS_CREATE, P.PROJECTS_EDIT,
  // Documents — department
  P.DOCUMENTS_VIEW_DEPARTMENT,
  // Attendance — own
  P.ATTENDANCE_VIEW_OWN, P.ATTENDANCE_EDIT_OWN,
  // Leave — own
  P.LEAVE_VIEW_OWN, P.LEAVE_CREATE_OWN, P.LEAVE_EDIT_OWN,
  P.TRAININGS_VIEW,
];

const EMPLOYEE_PERMISSIONS = [
  P.COMMUNICATION_VIEW_OWN, P.COMMUNICATION_CREATE_OWN,
  P.DASHBOARD_VIEW_OWN,
  // Employees — own profile
  P.EMPLOYEES_VIEW_OWN, P.EMPLOYEES_EDIT_OWN,
  // Contracts — own
  P.CONTRACTS_VIEW_OWN,
  // Attendance — own
  P.ATTENDANCE_VIEW_OWN, P.ATTENDANCE_EDIT_OWN,
  // Leave — own
  P.LEAVE_VIEW_OWN, P.LEAVE_CREATE_OWN, P.LEAVE_EDIT_OWN,
  // Payroll — own
  P.PAYROLL_VIEW_OWN,
  // Performance — own
  P.PERFORMANCE_VIEW_OWN,
  // Documents — own
  P.DOCUMENTS_VIEW_OWN,
  // Reports — own
  P.REPORTS_VIEW_OWN,
  // Projects — own tasks
  P.PROJECTS_VIEW_OWN,
  // Warnings — own
  P.WARNINGS_VIEW_OWN,
  // Assets — own: çka mban, jo çka ka kompania
  P.ASSETS_VIEW_OWN,
  P.TRAININGS_VIEW,
];

const AUDITOR_PERMISSIONS = [
  P.ONBOARDING_VIEW,
  P.DASHBOARD_VIEW_ALL,
  // View-only across all modules
  P.EMPLOYEES_VIEW_ALL, P.EMPLOYEES_EXPORT,
  P.CONTRACTS_VIEW_COMPANY, P.CONTRACTS_EXPORT,
  P.ATTENDANCE_VIEW_COMPANY, P.ATTENDANCE_EXPORT,
  P.LEAVE_VIEW_COMPANY, P.LEAVE_EXPORT,
  P.LEAVE_ADJUSTMENTS_VIEW,
  P.PAYROLL_VIEW_COMPANY, P.PAYROLL_EXPORT,
  P.PERFORMANCE_VIEW_COMPANY,
  P.DOCUMENTS_VIEW_COMPANY, P.DOCUMENTS_EXPORT,
  P.REPORTS_VIEW_ALL, P.REPORTS_EXPORT,
  P.AUDIT_LOGS_VIEW_ALL, P.AUDIT_LOGS_EXPORT,
  P.WARNINGS_VIEW_COMPANY,
  P.ASSETS_VIEW_COMPANY, P.ASSETS_EXPORT,
  P.COMPLIANCE_VIEW,
  P.RECRUITMENT_VIEW,
  P.TRAININGS_VIEW, P.TRAININGS_EXPORT,
];

// ── Roles registry ────────────────────────────────────────────────────────────
export const ROLES_CONFIG = {
  [ROLES.SUPER_ADMIN]: {
    key:         ROLES.SUPER_ADMIN,
    label:       'Super Admin',
    description: 'Akses i plotë pa kufizime. Menaxhon kompanitë dhe konfigurimin global.',
    priority:    100,
    color:       '#7c3aed',
    bgColor:     '#f3e8ff',
    aliases:     ['super_admin', 'superadmin', 'super admin'],
    permissions: SUPER_ADMIN_PERMISSIONS,
  },
  [ROLES.COMPANY_ADMIN]: {
    key:         ROLES.COMPANY_ADMIN,
    label:       'Company Admin',
    description: 'Administrator i kompanisë. Akses i plotë brenda organizatës.',
    priority:    90,
    color:       '#dc2626',
    bgColor:     '#fee2e2',
    // NOTE: 'menaxher'/'manager' are deliberately NOT here. The DB CHECK treats
    // 'manager' as a tier distinct from 'admin', and the backend
    // (normalizeRole → 'manager' → HR_MANAGER_PERMISSIONS) enforces them at the
    // HR-manager level. Listing them here made resolveRoleKey('manager') return
    // company_admin, so a manager saw the full admin UI while every admin action
    // 403'd on the backend. They belong to HR_MANAGER below, matching 'hr'.
    aliases:     ['company_admin', 'admin', 'administrator', 'company admin'],
    permissions: COMPANY_ADMIN_PERMISSIONS,
  },
  [ROLES.HR_MANAGER]: {
    key:         ROLES.HR_MANAGER,
    label:       'HR Manager',
    description: 'Menaxher i Burimeve Njerëzore. Kontrollon stafin, pushimet, recruitment.',
    priority:    70,
    color:       '#0891b2',
    bgColor:     '#e0f2fe',
    // 'menaxher'/'manager' resolve here (not company_admin) to match the
    // backend's manager tier — see COMPANY_ADMIN above.
    aliases:     ['hr_manager', 'hr manager', 'hr', 'human resources', 'menaxher', 'manager'],
    permissions: HR_MANAGER_PERMISSIONS,
  },
  [ROLES.PAYROLL_MANAGER]: {
    key:         ROLES.PAYROLL_MANAGER,
    label:       'Payroll Manager',
    description: 'Menaxher i pagave. Gjeneron dhe miraton pagat.',
    priority:    65,
    color:       '#059669',
    bgColor:     '#d1fae5',
    aliases:     ['payroll_manager', 'payroll manager', 'payroll'],
    permissions: PAYROLL_MANAGER_PERMISSIONS,
  },
  [ROLES.RECRUITER]: {
    key:         ROLES.RECRUITER,
    label:       'Recruiter',
    description: 'Specialist i rekrutimit. Menaxhon konkurset dhe aplikimet.',
    priority:    50,
    color:       '#d97706',
    bgColor:     '#fef3c7',
    aliases:     ['recruiter', 'recruitment', 'agent', 'agjent'],
    permissions: RECRUITER_PERMISSIONS,
  },
  [ROLES.TEAM_LEAD]: {
    key:         ROLES.TEAM_LEAD,
    label:       'Team Lead',
    description: 'Udhëheqës ekipi. Shikon dhe miraton pushimet dhe prezencën e ekipit.',
    priority:    40,
    color:       '#7c3aed',
    bgColor:     '#ede9fe',
    aliases:     ['team_lead', 'team lead', 'teamlead', 'lead'],
    permissions: TEAM_LEAD_PERMISSIONS,
  },
  [ROLES.EMPLOYEE]: {
    key:         ROLES.EMPLOYEE,
    label:       'Employee',
    description: 'Punonjës standard. Akses vetëm te të dhënat e veta.',
    priority:    10,
    color:       '#64748b',
    bgColor:     '#f1f5f9',
    aliases:     ['employee', 'staff', 'worker', 'punonjës', 'punojës'],
    permissions: EMPLOYEE_PERMISSIONS,
  },
  [ROLES.AUDITOR]: {
    key:         ROLES.AUDITOR,
    label:       'Auditor / Viewer',
    description: 'Lexim i plotë pa mundësi modifikimi. Ideal për auditim dhe raportim.',
    priority:    20,
    color:       '#0f172a',
    bgColor:     '#f8fafc',
    aliases:     ['auditor', 'viewer', 'audit', 'readonly'],
    permissions: AUDITOR_PERMISSIONS,
  },
};

/**
 * Resolve the canonical role key from a raw role string coming from the DB.
 * Returns null if no match found.
 */
export function resolveRoleKey(rawRole) {
  if (!rawRole) return null;
  const normalized = rawRole.toString().toLowerCase().trim();
  for (const [key, cfg] of Object.entries(ROLES_CONFIG)) {
    if (cfg.aliases.includes(normalized)) return key;
  }
  return null;
}

/**
 * Get merged unique permissions for an array of raw role strings.
 */
export function getPermissionsForRoles(rawRoles = []) {
  const set = new Set();
  rawRoles.forEach((raw) => {
    const key = resolveRoleKey(raw);
    if (key && ROLES_CONFIG[key]) {
      ROLES_CONFIG[key].permissions.forEach((perm) => set.add(perm));
    }
  });
  return Array.from(set);
}

export default ROLES_CONFIG;
